import React, { useState, useEffect } from 'react';
import { X, Mail, MessageSquare, Send, CheckCircle, AlertCircle, Clock, History } from 'lucide-react';
import { notificationsAPI } from '../services/api';
import toast from 'react-hot-toast';

interface NotificationHistory {
    _id: string;
    channel: string;
    status: string;
    title: string;
    message: string;
    sentAt?: string;
    createdAt: string;
    errorMessage?: string;
}

interface Props {
    orderId: string;
    orderNumber: string;
    orderStatus: string;
    customerName: string;
    customerEmail?: string;
    customerPhone?: string;
    onClose: () => void;
}

const STATUS_LABELS: Record<string, string> = {
    received: 'Received',
    in_progress: 'In Progress',
    ready: 'Ready for Collection',
    out_for_delivery: 'Out for Delivery',
    completed: 'Completed',
    cancelled: 'Cancelled',
};

const QUICK_MESSAGES: Record<string, string> = {
    received: 'We have received your order and it is being processed.',
    in_progress: 'Your items are currently being cleaned and prepared.',
    ready: 'Your order is ready for collection! Please visit us at your earliest convenience.',
    out_for_delivery: 'Your order is on its way! Our driver will be with you shortly.',
    completed: 'Your order has been completed. Thank you for choosing us!',
    cancelled: 'Your order has been cancelled. Please contact us if you have any questions.',
};

const SendNotificationModal: React.FC<Props> = ({
    orderId, orderNumber, orderStatus, customerName, customerEmail, customerPhone, onClose
}) => {
    const [channels, setChannels] = useState<string[]>([]);
    const [customMessage, setCustomMessage] = useState(QUICK_MESSAGES[orderStatus] || '');
    const [sending, setSending] = useState(false);
    const [config, setConfig] = useState<{ email: boolean; sms: boolean }>({ email: false, sms: false });
    const [history, setHistory] = useState<NotificationHistory[]>([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);

    useEffect(() => {
        // Fetch notification config
        notificationsAPI.getConfig()
            .then(res => {
                setConfig(res.data);
                // Auto-select available channels
                const auto: string[] = [];
                if (res.data.email && customerEmail) auto.push('email');
                if (res.data.sms && customerPhone) auto.push('sms');
                setChannels(auto);
            })
            .catch(() => { /* config unavailable */ });
    }, [customerEmail, customerPhone]);

    const fetchHistory = async () => {
        setLoadingHistory(true);
        try {
            const res = await notificationsAPI.getOrderHistory(orderId);
            setHistory(res.data);
        } catch { /* ignore */ }
        setLoadingHistory(false);
    };

    const toggleChannel = (ch: string) => {
        setChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);
    };

    const handleSend = async () => {
        if (channels.length === 0) {
            toast.error('Select at least one notification channel');
            return;
        }
        setSending(true);
        try {
            const res = await notificationsAPI.sendOrderNotification(orderId, {
                channels,
                customMessage: customMessage || undefined,
            });
            const data = res.data;
            if (data.errors && data.errors.length > 0) {
                data.errors.forEach((err: any) => toast.error(`${err.channel}: ${err.error}`));
            }
            if (data.results?.email?.success) toast.success('Email sent successfully');
            if (data.results?.sms?.success) toast.success('SMS sent successfully');
            if (data.results?.email?.success || data.results?.sms?.success) {
                setTimeout(onClose, 1500);
            }
        } catch (error: any) {
            const msg = error.response?.data?.error || 'Failed to send notification';
            toast.error(msg);
            if (error.response?.data?.details) {
                error.response.data.details.forEach((d: any) => toast.error(`${d.channel}: ${d.error}`));
            }
        }
        setSending(false);
    };

    const formatDateTime = (d: string) =>
        new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Send Notification</h2>
                        <p className="text-sm text-gray-500">Order {orderNumber} &middot; {customerName}</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="px-6 py-4 space-y-5">
                    {/* Current Status */}
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-500">Current Status:</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary-800">
                            {STATUS_LABELS[orderStatus] || orderStatus}
                        </span>
                    </div>

                    {/* Channel Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Notification Channels</label>
                        <div className="grid grid-cols-2 gap-3">
                            {/* Email */}
                            <button
                                type="button"
                                onClick={() => config.email && customerEmail && toggleChannel('email')}
                                disabled={!config.email || !customerEmail}
                                className={`relative flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                                    channels.includes('email')
                                        ? 'border-blue-500 bg-blue-50'
                                        : !config.email || !customerEmail
                                        ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                        : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                                }`}
                            >
                                <Mail className={`w-5 h-5 ${channels.includes('email') ? 'text-blue-600' : 'text-gray-400'}`} />
                                <div className="text-left">
                                    <p className={`text-sm font-medium ${channels.includes('email') ? 'text-blue-900' : 'text-gray-700'}`}>Email</p>
                                    <p className="text-xs text-gray-500 truncate max-w-[120px]">
                                        {!config.email ? 'Not configured' : !customerEmail ? 'No email' : customerEmail}
                                    </p>
                                </div>
                                {channels.includes('email') && (
                                    <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-blue-500" />
                                )}
                            </button>

                            {/* SMS */}
                            <button
                                type="button"
                                onClick={() => config.sms && customerPhone && toggleChannel('sms')}
                                disabled={!config.sms || !customerPhone}
                                className={`relative flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                                    channels.includes('sms')
                                        ? 'border-green-500 bg-green-50'
                                        : !config.sms || !customerPhone
                                        ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                        : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                                }`}
                            >
                                <MessageSquare className={`w-5 h-5 ${channels.includes('sms') ? 'text-green-600' : 'text-gray-400'}`} />
                                <div className="text-left">
                                    <p className={`text-sm font-medium ${channels.includes('sms') ? 'text-green-900' : 'text-gray-700'}`}>SMS</p>
                                    <p className="text-xs text-gray-500 truncate max-w-[120px]">
                                        {!config.sms ? 'Not configured' : !customerPhone ? 'No phone' : customerPhone}
                                    </p>
                                </div>
                                {channels.includes('sms') && (
                                    <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-green-500" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Quick Message Templates */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Quick Templates</label>
                        <div className="flex flex-wrap gap-1.5">
                            {Object.entries(STATUS_LABELS).map(([key, label]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setCustomMessage(QUICK_MESSAGES[key] || '')}
                                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                                        customMessage === QUICK_MESSAGES[key]
                                            ? 'bg-primary-100 border-primary-300 text-primary-700'
                                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Custom Message */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Message (optional)</label>
                        <textarea
                            value={customMessage}
                            onChange={(e) => setCustomMessage(e.target.value)}
                            rows={3}
                            maxLength={320}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500 resize-none"
                            placeholder="Add a custom message to the notification..."
                        />
                        <p className="text-xs text-gray-400 mt-1">{customMessage.length}/320 characters</p>
                    </div>

                    {/* Notification History Toggle */}
                    <div>
                        <button
                            type="button"
                            onClick={() => { setShowHistory(!showHistory); if (!showHistory && history.length === 0) fetchHistory(); }}
                            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <History className="w-4 h-4" />
                            {showHistory ? 'Hide' : 'Show'} Notification History
                        </button>
                        {showHistory && (
                            <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                                {loadingHistory && <p className="text-sm text-gray-400">Loading...</p>}
                                {!loadingHistory && history.length === 0 && (
                                    <p className="text-sm text-gray-400">No notifications sent yet</p>
                                )}
                                {history.map((n) => (
                                    <div key={n._id} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg text-xs">
                                        {n.channel === 'email' ? (
                                            <Mail className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                                        ) : (
                                            <MessageSquare className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-gray-700 capitalize">{n.channel}</span>
                                                {n.status === 'sent' || n.status === 'delivered' ? (
                                                    <CheckCircle className="w-3 h-3 text-green-500" />
                                                ) : n.status === 'failed' ? (
                                                    <AlertCircle className="w-3 h-3 text-red-500" />
                                                ) : (
                                                    <Clock className="w-3 h-3 text-gray-400" />
                                                )}
                                                <span className="text-gray-400 ml-auto">{formatDateTime(n.sentAt || n.createdAt)}</span>
                                            </div>
                                            {n.errorMessage && (
                                                <p className="text-red-500 mt-0.5 truncate">{n.errorMessage}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={sending || channels.length === 0}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <Send className="w-4 h-4" />
                        {sending ? 'Sending...' : `Send ${channels.map(c => c === 'email' ? 'Email' : 'SMS').join(' & ')}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SendNotificationModal;
